import {AxiosError} from "axios";
import dayjs from "dayjs";
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import updateLocale from 'dayjs/plugin/updateLocale';

import {create} from 'zustand';

import {ActivityState, GetIntervalsParams, InitialActivityState} from "../model/activity.model";
import {ActivityService} from "../api/activity.service";
import {DATE_FORMAT} from "../constant/format";
import {APP_SETTINGS_LS_KEY} from "../constant/key";
import {downloadFile} from "../utils/downloadFile";
import {GroupBy} from "../enum/activity.enum";
import {authStore} from "./auth.store";
import {deepCopy} from "../utils/deepCopy";
import {getPaginationFetchMethods, getSimpleFetchMethods} from "./utils/asyncZustandMethods";
import {trackEvent} from "../utils/analytics";

dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(updateLocale);

const INTERVALS_PAGE_SIZE = 7;

function detectFirstDayOfWeekFromLocale(): number {
  try {
    const locale = new Intl.Locale(navigator.language || 'en-US');
    // `getWeekInfo()` is the standardized method; some browsers expose it as a `weekInfo` property.
    const info = (locale as any).getWeekInfo?.() ?? (locale as any).weekInfo;
    if (info?.firstDay) {
      // Intl returns 1..7 (Monday..Sunday); dayjs uses 0..6 (Sunday..Saturday).
      return info.firstDay === 7 ? 0 : info.firstDay;
    }
  } catch {
    // fall through
  }
  return 1; // Monday fallback for browsers without weekInfo
}

function applyWeekStart(weekStart: number) {
  dayjs.updateLocale('en', { weekStart });
}

function readStoredFirstDayOfWeek(): number | null {
  try {
    const raw = localStorage.getItem(APP_SETTINGS_LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const v = parsed?.firstDayOfWeek;
      if (typeof v === 'number' && v >= 0 && v <= 6) return v;
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

function persistFirstDayOfWeek(value: number | null) {
  try {
    const raw = localStorage.getItem(APP_SETTINGS_LS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (value === null) {
      delete parsed.firstDayOfWeek;
    } else {
      parsed.firstDayOfWeek = value;
    }
    localStorage.setItem(APP_SETTINGS_LS_KEY, JSON.stringify(parsed));
  } catch {
    // ignore write errors (private mode, quota)
  }
}

export function getFirstDayOfWeekPreference(): number | null {
  return readStoredFirstDayOfWeek();
}

const storedFirstDay = readStoredFirstDayOfWeek();
let activeWeekStart: number =
  typeof storedFirstDay === 'number' ? storedFirstDay : detectFirstDayOfWeekFromLocale();
applyWeekStart(activeWeekStart);

const initialStartDate: string = dayjs().startOf('week').format(DATE_FORMAT);
const initialEndDate: string = dayjs().endOf('week').format(DATE_FORMAT);

export function setFirstDayOfWeekPreference(value: number | null) {
  persistFirstDayOfWeek(value);
  const next =
    typeof value === 'number' ? value : detectFirstDayOfWeekFromLocale();
  if (next === activeWeekStart) return;
  activeWeekStart = next;
  applyWeekStart(next);
  activityStore.setState({ firstDayOfWeek: next });

  const params = activityStore.getState().intervalsParams;
  if (params.from === initialStartDate && params.to === initialEndDate) {
    activityStore.getState().setIntervalsParams({
      from: dayjs().startOf('week').format(DATE_FORMAT),
      to: dayjs().endOf('week').format(DATE_FORMAT),
    });
  }
}

const initialState: InitialActivityState = {
  filtersConfiguration: null,
  activityTypes: null,
  activities: null,
  intervalFilters: null,
  intervalToEdit: null,
  activityToEdit: null,
  activityToCreate: null,
  activityDetails: null,
  activityTags: null,

  intervalsParams: {
    from: initialStartDate,
    to: initialEndDate,
    tags: [],
    groupBy: GroupBy.DAY
  },
  firstDayOfWeek: activeWeekStart,
  intervalsPagination: {
    page: 0,
    size: INTERVALS_PAGE_SIZE
  },
  intervalsPaginationInfo: {
    last: true
  },

  tagsParams: {
    query: ''
  },
  activityTagsParams: {
    query: ''
  },
  tagsPagination: {
    page: 0
  },
  tagsPaginationInfo: {
    last: true
  },
  activityTagsPagination: {
    page: 0
  },
  activityTagsPaginationInfo: {
    last: true
  },

  filterTags: null,
  daysIntervals: null,
  statistics: null,
  loading: {
    getActivityDetails: false,
    getActivityTypes: false,
    getIntervalsFilters: false,
    getIntervalsStartPage: false,
    getNextActivityPage: false,
    getNextIntervalsPage: false,
    getStatistics: false,
    getActivities: false,
    startActivity: false,
    stopActivity: false,
    pauseActivity: false,
    resumeActivity: false,
    rewriteInterval: false,
    deleteInterval: false,
    deleteActivity: false,
    receiveExport: false,
    getTagsStartPage: false,
    getNextTagsPage: false,
    getActivityTagsStartPage: false,
    getActivityNextTagsPage: false,
    rewriteActivity: false,
    createActivity: false,
    createFilter: false,
    deleteFilter: false,
    updateFilter: false
  },
  error: {
    getActivityDetails: null,
    getActivityTypes: null,
    getIntervalsFilters: null,
    getIntervalsStartPage: null,
    getNextIntervalsPage: null,
    getStatistics: null,
    getActivities: null,
    startActivity: null,
    stopActivity: null,
    pauseActivity: null,
    resumeActivity: null,
    rewriteInterval: null,
    deleteInterval: null,
    deleteActivity: null,
    receiveExport: null,
    getTagsStartPage: null,
    getNextTagsPage: null,
    getActivityTagsStartPage: null,
    getActivityNextTagsPage: null,
    rewriteActivity: null,
    createActivity: null,
    createFilter: null,
    deleteFilter: null,
    updateFilter: null
  },
};

export const activityStore = create<ActivityState>((set, get) => ({
  setFiltersConfiguration: (filtersConfiguration) => set({ filtersConfiguration }),
  setIntervalToEdit: (interval) => set({ intervalToEdit: interval }),
  setActivityToEdit: (activity) => set({ activityToEdit: deepCopy(activity) }),
  setActivityToCreate: (activity) => set({ activityToCreate: deepCopy(activity) }),
  updateFilter: async (filter) => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'updateFilter');

    try {
      startLoading();

      await ActivityService.updateFilter(filter);

      await get().getIntervalsFilters();

      const updatedFilter = get().intervalFilters?.find((e) => e.id === filter.id);

      updatedFilter && get().setFiltersConfiguration(updatedFilter.name);

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>;
      endLoadingWithError(error);
    }
  },
  deleteFilter: async (id) => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'deleteFilter');

    try {
      startLoading();

      await ActivityService.deleteFilter(id);

      await get().getIntervalsFilters();

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>;
      endLoadingWithError(error);
    }
  },
  createFilter: async (filter) => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'createFilter');

    try {
      startLoading();

      await ActivityService.createFilter(filter);

      await get().getIntervalsFilters();

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>;
      endLoadingWithError(error);
    }
  },

  createActivity: async (activity) => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'createActivity');

    try {
      startLoading();

      await ActivityService.createActivity(activity);

      trackEvent('activity_create');

      get().setActivityToCreate(null);

      await Promise.all([get().getStatistics(), get().getIntervalsStartPage(), get().getActivities()]);

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>;
      endLoadingWithError(error);
    }
  },
  setTagsParams: (params) => set({ tagsParams: { ...get().tagsParams, ...params } }),
  setActivityTagsParams: (params) => set({activityTagsParams : { ...get().activityTagsParams, ...params } }),

  getTagsStartPage: async () => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getPaginationFetchMethods(activityStore, 'getTagsStartPage');
    try {
      startLoading({ tagsPagination: initialState.tagsPagination });

      const getTagsRes = await ActivityService.getActivityTags({ ...get().tagsPagination, ...get().tagsParams });

      endLoadingWithSuccess({ tagsPaginationInfo: { last: getTagsRes.data.last } }, 'filterTags', getTagsRes.data.content);
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  getNextTagsPage: async () => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getPaginationFetchMethods(activityStore, 'getNextTagsPage');
    try {
      startLoading({
        tagsPagination: { page: Number(get().tagsPagination.page + 1) }
      });

      const getTagsRes = await ActivityService.getActivityTags({ ...get().tagsPagination, ...get().tagsParams });

      const existingData = get().filterTags || []

      endLoadingWithSuccess({ tagsPaginationInfo: { last: getTagsRes.data.last } }, 'filterTags', [...existingData, ...getTagsRes.data.content]);
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  getActivityTagsStartPage: async () => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getPaginationFetchMethods(activityStore, 'getActivityTagsStartPage');
    try {
      startLoading({ activityTagsPagination: initialState.activityTagsPagination });

      const getTagsRes = await ActivityService.getActivityTags({ ...get().activityTagsPagination, ...get().activityTagsParams });

      endLoadingWithSuccess({ activityTagsPaginationInfo: { last: getTagsRes.data.last } }, 'activityTags', getTagsRes.data.content);
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  getActivityNextTagsPage: async () => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getPaginationFetchMethods(activityStore, 'getActivityNextTagsPage');
    try {
      startLoading({
        activityTagsPagination: { page: Number(get().activityTagsPagination.page + 1) }
      });

      const getTagsRes = await ActivityService.getActivityTags({ ...get().activityTagsPagination, ...get().activityTagsParams });

      const existingData = get().activityTags || []

      endLoadingWithSuccess({ activityTagsPaginationInfo: { last: getTagsRes.data.last } }, 'activityTags', [...existingData, ...getTagsRes.data.content]);
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  getActivityDetails: async (activityId, onSuccess) => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'getActivityDetails');

    try {
      startLoading();

      const getActivityDetailsRes = await ActivityService.getActivity(activityId);

      if (onSuccess) {
        onSuccess(getActivityDetailsRes.data);
      }

      endLoadingWithSuccess('activityDetails', getActivityDetailsRes.data);
    } catch (err) {
      const error = err as AxiosError<Error>;

      endLoadingWithError(error);
    }
  },
  receiveExport: async () => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'receiveExport');
    try {
      startLoading();

      const params = get().intervalsParams;
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const receiveExportRes = params.from && params.to && userTimezone && await ActivityService.receiveExport({ from: params.from, to: params.to, timezone: userTimezone });

      if (receiveExportRes) {
        trackEvent('export');
        downloadFile(receiveExportRes.data)
      }

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>;
      endLoadingWithError(error);
    }
  },
  deleteInterval: async (interval) => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'deleteInterval');
    try {
      startLoading();

      await ActivityService.deleteInterval(interval);

      trackEvent('interval_delete');

      await Promise.all([get().getStatistics(), get().getIntervalsStartPage(), get().getActivities()]);

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>;

      endLoadingWithError(error);
    }
  },
  deleteActivity: async (activity) => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'deleteActivity');
    try {
      startLoading();

      await ActivityService.deleteActivity(activity.id);

      await Promise.all([get().getStatistics(), get().getIntervalsStartPage(), get().getActivities()]);

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>;

      endLoadingWithError(error);
    }
  },
  rewriteActivity: async (activity) => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'rewriteActivity');
    try {
      startLoading();

      await ActivityService.rewriteActivity(activity)

      trackEvent('activity_edit');

      get().setActivityToEdit(null);

      await Promise.all([get().getStatistics(), get().getIntervalsStartPage(), get().getActivities(), get().getTagsStartPage()]);

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  rewriteInterval: async (interval) => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'rewriteInterval');
    try {
      startLoading();

      await ActivityService.rewriteInterval(interval);

      trackEvent('interval_edit');

      get().setIntervalToEdit(null);

      await Promise.all([get().getStatistics(), get().getIntervalsStartPage()]);

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  resumeActivity: async (typeId) => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'resumeActivity');
    try {
      startLoading();

      await ActivityService.resumeActivity(typeId, Math.floor(Date.now() / 1000));

      trackEvent('activity_resume');

      await get().getActivities();

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  pauseActivity: async (typeId) => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'pauseActivity');
    try {
      startLoading();

      await ActivityService.pauseActivity(typeId, Math.floor(Date.now() / 1000));

      trackEvent('activity_pause');

      await get().getActivities();

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  stopActivity: async (typeId) => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'stopActivity');
    try {
      startLoading();

      await ActivityService.stopActivity(typeId, Math.floor(Date.now() / 1000));

      trackEvent('activity_stop');

      await get().getActivities();

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  refreshData: async() => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'refreshData');

    try {
      startLoading();

      await Promise.all([get().getStatistics(), get().getIntervalsStartPage(), get().getActivities()]);

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>;
      endLoadingWithError(error);
    }
  },

  startActivity: async (typeId) => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'startActivity');
    try {
      startLoading();

      await ActivityService.startActivity(typeId, Math.floor(Date.now() / 1000));

      trackEvent('activity_start');

      // Occurrences are instantly stopped (start === finish), so they won't
      // trigger the usual activitiesLength change that causes RecordsPage to
      // re-fetch. Force a data refresh for occurrence types.
      const activityType = get().activityTypes?.find(t => t.id === typeId);
      if (activityType?.occurrence) {
        await Promise.all([get().getStatistics(), get().getIntervalsStartPage(), get().getActivities()]);
      }

      endLoadingWithSuccess();
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  setIntervalsParams: (params: Partial<GetIntervalsParams>) => {
    set({
      intervalsParams: {
        ...get().intervalsParams,
        ...params
      }
    })
  },
  getActivities: async () => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'getActivities');
    try {
      startLoading();

      const getActivitiesRes = await ActivityService.getActivities();

      endLoadingWithSuccess('activities', getActivitiesRes.data.activities);
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  getStatistics: async () => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'getStatistics');
    try {
      startLoading()

      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const getStatisticsRes = await ActivityService.getStatistics({ ...get().intervalsParams, groupBy: get().intervalsParams.groupBy || GroupBy.DAY, timezone: userTimezone });

      endLoadingWithSuccess('statistics', getStatisticsRes.data);
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  getIntervalsFilters: async () => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'getIntervalsFilters');
    try {
      startLoading();

      const intervalsFilters = await ActivityService.getIntervalsFilters();

      endLoadingWithSuccess('intervalFilters', intervalsFilters.data)
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  getIntervalsStartPage: async () => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getPaginationFetchMethods(activityStore, 'getIntervalsStartPage');
    try {
      const fieldsToSet = {
        intervalsPagination: initialState.intervalsPagination
      };

      startLoading(fieldsToSet);

      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const getIntervalsRes = await ActivityService.getIntervals({ ...get().intervalsParams, timezone: userTimezone }, get().intervalsPagination);

      endLoadingWithSuccess({ intervalsPaginationInfo: { last: getIntervalsRes.data.last } }, 'daysIntervals', getIntervalsRes.data.content);
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  getNextIntervalsPage: async () => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getPaginationFetchMethods(activityStore, 'getNextIntervalsPage');
    try {
      const fieldsToSet = {
        intervalsPagination: {
          ...get().intervalsPagination,
          page: Number(get().intervalsPagination.page + 1)
        }
      };

      startLoading(fieldsToSet);

      const userTimezone = authStore.getState().user?.timeZone;

      const getIntervalsRes = await ActivityService.getIntervals({ ...get().intervalsParams, timezone: userTimezone }, get().intervalsPagination);

      const existingData = get().daysIntervals || []

      endLoadingWithSuccess({ intervalsPaginationInfo: { last: getIntervalsRes.data.last } }, 'daysIntervals', [...existingData, ...getIntervalsRes.data.content]);
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  getActivityTypes: async () => {
    const [startLoading, endLoadingWithSuccess, endLoadingWithError] = getSimpleFetchMethods(activityStore, 'getActivityTypes');
    try {
      startLoading();

      const activityTypesRes = await ActivityService.getActivityTypes();

      endLoadingWithSuccess('activityTypes', activityTypesRes.data);
    } catch (err) {
      const error = err as AxiosError<Error>
      endLoadingWithError(error);
    }
  },
  ...initialState,
}));
